// Per-act, per-difficulty boss rosters.
// One source of truth for who shows up, at what level, and at what star tier.
// Edit any single line to fine-tune that specific fight.

import type { ChampionId } from "./champions";
import type { Difficulty } from "./difficulty";
import type { StarLevel } from "../game/RunState";

export interface BossChampion {
  id: ChampionId;
  level: number;
  stars: StarLevel | 4;
}

/** Index 0 = Act 1, ... index 6 = Act 7. */
export const BOSS_COMPS: Record<Difficulty, BossChampion[][]> = {
  normal: [
    // Act 1 — Freljord
    [
      { id: "Tryndamere", level: 3, stars: 1 },
      { id: "Ashe",       level: 3, stars: 1 },
    ],
    // Act 2 — Demacia
    [
      { id: "Garen", level: 6, stars: 2 },
      { id: "Vayne", level: 6, stars: 1 },
      { id: "Lux",   level: 6, stars: 1 },
    ],
    // Act 3 — Noxus
    [
      { id: "Darius",   level: 8, stars: 2 },
      { id: "Katarina", level: 8, stars: 1 },
      { id: "Draven",   level: 8, stars: 2 },
    ],
    // Act 4 — Piltover & Zaun
    [
      { id: "Vi",      level: 10, stars: 2 },
      { id: "Caitlyn", level: 12, stars: 2 },
      { id: "Jinx",    level: 12, stars: 3 },
    ],
    // Act 5 — Ixtal
    [
      { id: "Malphite", level: 11, stars: 3 },
      { id: "Nidalee",  level: 11, stars: 2 },
      { id: "Neeko",    level: 11, stars: 2 },
    ],
    // Act 6 — Shurima
    [
      { id: "Nasus", level: 13, stars: 3 },
      { id: "Sivir", level: 13, stars: 2 },
      { id: "Azir",  level: 13, stars: 3 },
    ],
    // Act 7 — Targon
    [
      { id: "Leona",       level: 14, stars: 3 },
      { id: "Soraka",      level: 14, stars: 3 },
      { id: "AurelionSol", level: 14, stars: 3 },
    ],
  ],
  hard: [
    // Act 1 — Freljord
    [
      { id: "Tryndamere", level: 2, stars: 2 },
      { id: "Ashe",       level: 2, stars: 2 },
    ],
    // Act 2 — Demacia
    [
      { id: "Garen", level: 5, stars: 3 },
      { id: "Vayne", level: 5, stars: 2 },
      { id: "Lux",   level: 5, stars: 2 },
    ],
    // Act 3 — Noxus  (hard had per-champ caps: nothing bumps)
    [
      { id: "Darius",   level: 7, stars: 2 },
      { id: "Katarina", level: 8, stars: 2 },
      { id: "Draven",   level: 7, stars: 2 },
    ],
    // Act 4 — Piltover & Zaun
    [
      { id: "Vi",      level: 10, stars: 3 },
      { id: "Caitlyn", level: 10, stars: 3 },
      { id: "Jinx",    level: 11, stars: 4 },
    ],
    // Act 5 — Ixtal
    [
      { id: "Malphite", level: 10, stars: 4 },
      { id: "Nidalee",  level: 10, stars: 3 },
      { id: "Neeko",    level: 10, stars: 3 },
    ],
    // Act 6 — Shurima
    [
      { id: "Nasus", level: 12, stars: 4 },
      { id: "Sivir", level: 12, stars: 3 },
      { id: "Azir",  level: 12, stars: 4 },
    ],
    // Act 7 — Targon
    [
      { id: "Leona",       level: 13, stars: 4 },
      { id: "Soraka",      level: 13, stars: 4 },
      { id: "AurelionSol", level: 13, stars: 4 },
    ],
  ],
   impossible: [
    // Act 1 — Freljord
    [
      { id: "Tryndamere", level: 3, stars: 2 },
      { id: "Ashe",       level: 3, stars: 2 },
    ],
    // Act 2 — Demacia
    [
      { id: "Garen", level: 7, stars: 3 },
      { id: "Vayne", level: 7, stars: 2 },
      { id: "Lux",   level: 7, stars: 2 },
    ],
    // Act 3 — Noxus  (hard had per-champ caps: nothing bumps)
    [
      { id: "Darius",   level: 9, stars: 2 },
      { id: "Katarina", level: 9, stars: 2 },
      { id: "Draven",   level: 9, stars: 2 },
    ],
    // Act 4 — Piltover & Zaun
    [
      { id: "Vi",      level: 13, stars: 3 },
      { id: "Caitlyn", level: 13, stars: 3 },
      { id: "Jinx",    level: 13, stars: 4 },
    ],
    // Act 5 — Ixtal
    [
      { id: "Malphite", level: 15, stars: 4 },
      { id: "Nidalee",  level: 15, stars: 3 },
      { id: "Neeko",    level: 15, stars: 3 },
    ],
    // Act 6 — Shurima
    [
      { id: "Nasus", level: 17, stars: 4 },
      { id: "Sivir", level: 17, stars: 3 },
      { id: "Azir",  level: 13, stars: 4 },
    ],
    // Act 7 — Targon
    [
      { id: "Leona",       level: 18, stars: 4 },
      { id: "Soraka",      level: 18, stars: 4 },
      { id: "AurelionSol", level: 18, stars: 4 },
    ],
  ],
};

export function getBossComp(difficulty: Difficulty, actNumber: number): BossChampion[] | undefined {
  return BOSS_COMPS[difficulty]?.[actNumber - 1];
}
